import { GridColDef } from '@mui/x-data-grid';
import {
  Box,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { atom, useSetAtom } from 'jotai';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { COLORS } from '../../../theme/themeConstants';
import { useCallback, useMemo, useState,useEffect } from 'react';
import EditStoreOwnerModal from '../EditStoreOwnerModal';
import AddChoice from './AddChoice';
import { GridRowId } from '@mui/x-data-grid';
import {
  useAllChoices,
  useDeleteChoice,
  usePatchChoice,
} from '../../Query/ChoiceQuery';
import React from 'react';
import { useSnackbar } from '../../Stateless/SnackBarProvider';
import { useManageCmsParams } from '../ManageCMS/useManageCmsParams';
import CommonDataGrid from '../../Stateless/CommonDataGrid';
import ErrorHandler from '../../Stateless/Common/ErrorHandler';
import { useDebounce } from '../Utils/useDounce';
import PermissionActionButtons from '../../Stateless/Common/PermissionActionButtons';
import { usePermissions } from '../../../context/PermissionContext';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export interface ChoiceDataType {
  storeType: any;
  id?: any;
  choiceId?: string;
  choiceNameEnglish?: string;
  choiceNameArabic?: string;
  price: number;
  merchantId?: string;
  merchantName?: string;
  // storeType?: string;
  storeId?: string;
}

export const choiceAtom = atom<ChoiceDataType | null>(null);

type StoreWithSno = {
  id: string;
  sno: number;
  name: string;
  storeType: string;
  choice: string;
  price: number;
  status: string;
};


const ManageChoicesTable = () => {
  const { showMessage } = useSnackbar();
  const setChoiceAtom = useSetAtom(choiceAtom);
  const { hasPermission } = usePermissions();
  // Keep permissions intact; we'll show UI conditionally without changing permission logic
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const snackbarMessage = ''; // Replace with actual message
  const snackbarSeverity: 'success' | 'error' | 'info' | 'warning' = 'info';
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [modalContent] = useState<'add' | 'edit'>('edit');
  
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const { params, dispatch } = useManageCmsParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);
  const { data: choiceData, isLoading, refetch } = useAllChoices(memoizedParams);

  const { mutate: patchChoice } = usePatchChoice();

  const { mutate: deleteChoice } = useDeleteChoice();


  const filteredRows = React.useMemo(() => {
    const storeOwners = choiceData?.data || [];

    // Add sno field to each row and ensure all required fields are present
    return storeOwners.map((row, index) => {
      // Ensure we have a valid row object
      if (!row) return { id: `empty-${index}`, sno: index + 1 } as StoreWithSno;

      // Create a new object with all the row properties plus sno
      const rowWithSno = {
        ...row,
        id: row.choiceId,
        sno: index + 1 + (Number(params.page - 1)) * Number(params.limit),
        name: row.storeName,
        storeType: row.storeType,
        choice: row.choiceNameEnglish,
        price: row.price,
        status: row.status,
      };

      return rowWithSno as StoreWithSno;
    });
  }, [params.search, choiceData, params.page, params.limit]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value)
    };
  
    useEffect(() => {
      dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
    }, [debouncedSearch]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  const handleBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one choice to block', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to block?');
      if (!confirmed) return;

      patchChoice({
        id: selectedRows,
        data: { status: 'blocked' },
      });
    }
  };

  const handleUnBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one choice to unblock', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to unblock?');
      if (!confirmed) return;

      patchChoice({
        id: selectedRows,
        data: { status: 'unblocked' },
      });
    }
  };

  const handleDeleteClick = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one choice to delete', 'error');
      return;
    }
    
    const confirmed = window.confirm('Are you sure you want to delete?');
    if (!confirmed) return;

    // Clear selected rows immediately after confirmation
    setSelectedRows([]);
    
    deleteChoice(selectedRows, {
      onSuccess: () => {
        showMessage('choice deleted successfully', 'success');
        // Refetch data to update the table
        refetch();
      },
      onError: () => {
        showMessage('Error deleting choice', 'error');
        // If there's an error, we might want to restore the selection
        // setSelectedRows(selectedRows); // Uncomment if you want to restore selection on error
      },
    });
  }, [selectedRows, showMessage, deleteChoice, refetch]);

  const handleBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to block this Choice?'
    );
    if (!confirmed) return;

    await patchChoice({
      id: [id],
      data: { status: 'blocked' },
    });
  };
  const handleUnBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to unblock this Choice?'
    );
    if (!confirmed) return;

    await patchChoice({
      id: [id],
      data: { status: 'unblocked' },
    });
  };

  const handleDeleteButton = useCallback(
    (id: string) => {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      deleteChoice([id], {
        onSuccess: () => {
          showMessage('choice deleted successfully', 'success');
          refetch();
        },
        onError: () => {
          showMessage('Error deleting choice', 'error');
        },
      });
    },
    [deleteChoice, showMessage, refetch]
  );

  const handleAddSubAdmin = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleEditClick = (row: any) => {
    setChoiceAtom(row);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setChoiceAtom(null);
  };

  const handleCategorySelection = (selectionModel: GridRowId[]) => {
      const ids = selectionModel.map((id) => String(id));
      setSelectedRows(ids);
    };

  // Compute which bulk actions to show based on selected rows' statuses
  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = choiceData?.data || [];
    const idToStatus = new Map<string, string>(
      dataList.map((item: any) => [String(item.choiceId), String(item.status || '')])
    );
    const statuses = selectedRows.map((id) => idToStatus.get(String(id)) || '');
    const hasBlocked = statuses.some((s) => s === 'blocked');
    const hasUnblocked = statuses.some((s) => s === 'unblocked');
    return {
      showBlock: hasUnblocked,
      showUnblock: hasBlocked,
    };
  }, [selectedRows, choiceData]);

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => setAnchorEl(null);

    const id = typeof row.id === 'string' ? row.id : '';

    const canEdit = hasPermission('choices', 'Edit');
    const canDelete = hasPermission('choices', 'Delete');

    if (!canEdit && !canDelete) {
      return null;
    }

    return (
      <>
        <IconButton size="small" onClick={handleOpen}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {canEdit && (
            <MenuItem
              onClick={() => {
                handleClose();
                handleEditClick(row);
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem
              onClick={() => {
                handleClose();
                if (id) {
                  handleDeleteButton(id);
                }
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'sno',
      headerName: 'S.No',
      flex: 0.3, // small number
    },
    {
      field: 'name',
      headerName: 'Store Name',
      flex: 2, // moderately sized
    },
    {
      field: 'storeType',
      headerName: 'Store Type',
      flex: 2, // moderately sized
    },
    {
      field: 'choice',
      headerName: 'Choices',
      flex: 2, // usually a long string
    },
    {
      field: 'price',
      headerName: 'Price in (JOD)',
      flex: 1, // short value, but still needs some room
    },
    {
      field: 'addedBy',
      headerName: 'Added By',
      flex: 1, // moderate length, like name
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const isBlocked = params.value === 'blocked';
        const id = params.row.id;
        const validId = typeof id === 'string' ? id : '';

        const canBlock = hasPermission('choices', 'Block');
        const canUnblock = hasPermission('choices', 'Unblock');

        // Hide status toggle entirely if user lacks relevant permission
        if ((isBlocked && !canUnblock) || (!isBlocked && !canBlock)) {
          return null;
        }

        return (
          <IconButton
            size="small"
            sx={{ color: isBlocked ? '#f44336' : '#4caf50' }}
            onClick={() =>
              isBlocked
                ? handleUnBlockClickButton(validId)
                : handleBlockClickButton(validId)
            }
          >
            {isBlocked ? (
              <BlockIcon fontSize="small" />
            ) : (
              <CheckCircleIcon fontSize="small" />
            )}
          </IconButton>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => <RowActions row={params.row} />,
    },
  ];

  return (
    <>
      <Box
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          marginTop: '20px',
          color: '#666',
        }}
      >
        {snackbarOpen && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            >
              {snackbarMessage}
            </Alert>
          </Box>
        )}

        <Box
          sx={{
            // p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2, // Optional: adds spacing between wrapped items
          }}
        >
          {/* Search - Left */}
          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search by name, email, mobile"
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none', // remove inner border
                },
                '& fieldset': {
                  border: 'none', // remove Material-UI default border
                },
                '& .MuiInputBase-input': {
                  paddingTop: '10px', // adjust this value to move the placeholder lower
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Action Buttons + Add Button - Right */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <PermissionActionButtons
              module="choices"
              onBlock={showBlock ? handleBlockClick : undefined}
              onUnblock={showUnblock ? handleUnBlockClick : undefined}
              onDelete={selectedRows.length ? handleDeleteClick : undefined}
              blockLabel={showBlock ? 'Block' : undefined}
              unblockLabel={showUnblock ? 'Unblock' : undefined}
            />
            {hasPermission('choices', 'Add') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSubAdmin}
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  bgcolor: COLORS.primary.main,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: COLORS.primary.main,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                Add Choice
              </Button>
            )}
          </Box>
        </Box>

        <EditStoreOwnerModal
          open={openAddDialog}
          onClose={handleCloseAddDialog}
          title="Add Choice"
        >
          <AddChoice onCancel={handleCloseAddDialog} />
        </EditStoreOwnerModal>

        <EditStoreOwnerModal
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          title={modalContent === 'add' ? 'Add Choice' : 'Edit Choice'}
        >
          <AddChoice onCancel={handleCloseEditDialog} />
        </EditStoreOwnerModal>

        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={filteredRows || []}
          columns={columns}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={choiceData?.pagination?.totalItems}
          components={{
            NoRowsOverlay: () => <ErrorHandler type="no-data" message="No categories found" onRetry={() => refetch()} />
          }}
          onRowSelectionModelChange={handleCategorySelection}
          selectionModel={selectedRows}
        />
      </Box>
    </>
  );
};

export default ManageChoicesTable;
